import React, { createContext, useState, useContext, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'MANAGER' | 'STAFF';
  organizationId?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const GQL_HTTP_URL = import.meta.env.VITE_GQL_HTTP || 'http://localhost:4000/graphql';

const LOGIN_MUTATION = `
  mutation MobileLogin($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      accessToken
      user {
        id
        email
        name
        role
        organizationId
        organization {
          id
          name
          slug
        }
      }
    }
  }
`;

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('sakura_user');
    return storedUser ? (JSON.parse(storedUser) as User) : null;
  });
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('sakura_token');
  });

  const login = async (email: string, password: string) => {
    const response = await fetch(GQL_HTTP_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: LOGIN_MUTATION,
        variables: { email, password },
      }),
    });

    const payload = await response.json();

    if (!response.ok || payload?.errors?.length || !payload?.data?.login) {
      throw new Error(payload?.errors?.[0]?.message || 'Invalid email or password');
    }

    const loginData = payload.data.login;
    setUser(loginData.user);
    setToken(loginData.accessToken);

    localStorage.setItem('sakura_token', loginData.accessToken);
    localStorage.setItem('sakura_user', JSON.stringify(loginData.user));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('sakura_token');
    localStorage.removeItem('sakura_user');
  };

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated: !!token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
